import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, Loader2, AlertTriangle, Anchor, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { setPassword as apiSetPassword } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const MIN_LEN = 12;

export default function SetPassword() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < MIN_LEN) {
      toast.error(`Minimum ${MIN_LEN} characters`);
      return;
    }
    if (pw !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setBusy(true);
    try {
      await apiSetPassword(pw);
      await refresh();
      toast.success("Password anchored. Welcome to the Sentinel.");
      navigate(user?.is_architect ? "/console" : "/welcome");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Failed to set password";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[#09090b] text-zinc-100 px-6"
      data-testid="set-password-page"
    >
      <div className="max-w-lg w-full">
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.35em] text-emerald-400">
          <Anchor className="h-3 w-3" /> First Handshake
        </div>
        <h1 className="font-heading text-4xl mt-2">Anchor your password.</h1>
        <p className="text-sm text-zinc-500 mt-3 leading-relaxed">
          Google verified your email. Now seal your workspace with a password
          that only you know. The Sentinel stores it as a bcrypt hash — not
          even the Architect can read it. Minimum {MIN_LEN} characters.
        </p>

        <div className="mt-6 border border-amber-500/30 bg-amber-500/5 p-4 flex gap-3">
          <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
          <div className="text-[11px] text-amber-200/80 leading-relaxed">
            If you forget it, file a reset request from the Unlock page —
            the Architect will approve it and deliver a temp password
            out-of-band. Write yours somewhere safe before you click Anchor.
          </div>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="set-password-form">
          <div>
            <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-2">
              <KeyRound className="h-3 w-3" /> New password
            </label>
            <div className="mt-2 flex gap-2">
              <input
                type={show ? "text" : "password"}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                minLength={MIN_LEN}
                autoFocus
                autoComplete="new-password"
                data-testid="set-password-input"
                className="flex-1 bg-zinc-950 border border-zinc-800 focus:border-emerald-500 focus:outline-none text-sm p-3 font-body text-zinc-100"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="px-3 border border-zinc-800 text-zinc-400 hover:border-zinc-600"
              >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <div className="mt-1 text-[10px] text-zinc-600">
              {pw.length}/{MIN_LEN} characters minimum
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              Confirm
            </label>
            <input
              type={show ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              data-testid="set-password-confirm-input"
              className="mt-2 w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500 focus:outline-none text-sm p-3 font-body text-zinc-100"
            />
          </div>

          <button
            type="submit"
            disabled={busy}
            data-testid="set-password-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 border border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-zinc-950 disabled:opacity-50 text-xs uppercase tracking-[0.3em]"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
            {busy ? "Anchoring…" : "Anchor Password"}
          </button>
        </form>
      </div>
    </div>
  );
}
